import { faBuilding, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, DialogActions, DialogContent, TextField
} from '@mui/material';
import {
  CustomTooltip, FlexContainer, FormAddress, FormCountryIdentifier, FormLine, SelectField, SkeletonForm
} from 'components';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { translate, UserHelper } from 'utils/helpers';

import { APPLICATION_ROLES, getLegalEntityTypeTranslationKey, LEGAL_ENTITY_TYPES } from 'utils/constants';

import { ModalProps } from 'hooks/ModalProvider';
import { EntityView } from 'models';
import { LegalEntityService } from 'services';
import { RequiredSelectHack } from '../RequiredSelectHack/RequiredSelectHack';
import { ModalHeader } from './ModalHeader';

export const CreateSupplierOrRecipientModal = ({
                                                 entityId, onClose, onSubmit
                                               }: ModalProps<EntityView> & {
  entityId: string;
}) => {
  // Initialize react-hook-form with an empty object to recreate  react-use-form-state behaviour
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    clearErrors,
    reset,
    formState: { errors }
  } = useForm<EntityView>({
    defaultValues: {} // IMPORTANT:empty object instead new EntityView()
  });

  const [isLoading, setLoading] = useState(false);
  const [identifierFields, setIdentifierFields] = useState([]);
  const canHandleRecipient = UserHelper.hasAccessRight([APPLICATION_ROLES.DISTRIBUTOR_ADMIN, APPLICATION_ROLES.SUPER_ADMIN]);
  // Watch entityTypes to trigger re-renders when it changes
  const entityTypes = watch('entityTypes');

  const legalEntityTypeToSelectItem = (entityType: string) => ({
    value: entityType,
    label: translate(getLegalEntityTypeTranslationKey(entityType))
  });

  useEffect(() => {
    if (entityId) {
      setLoading(true);
      LegalEntityService.getLegalEntity(entityId, [LEGAL_ENTITY_TYPES.SUPPLIER, LEGAL_ENTITY_TYPES.RECIPIENT])
          .then(response => {
            Object.entries(response).forEach(([key, value]) => {
              if (value !== undefined && value !== null) {
                setValue(key as any, value);
              }
            });
            if (response.address && typeof response.address !== 'string') {
              if (response.address?.address1) setValue('address1', response.address.address1);
              if (response.address?.address2) setValue('address2', response.address.address2);
              if (response.address?.city) setValue('city', response.address.city);
              if (response.address?.country) setValue('country', response.address.country);
              if (response.address?.postalCode) setValue('postalCode', response.address.postalCode);
            }
          })
          .finally(() => { setLoading(false); });
    } else {
      reset({});
    }
    if (!canHandleRecipient && !entityId) {
      setValue('entityTypes', [{ value: LEGAL_ENTITY_TYPES.SUPPLIER, label: translate(getLegalEntityTypeTranslationKey(LEGAL_ENTITY_TYPES.SUPPLIER)) }]);
    }
  }, [canHandleRecipient, entityId, reset, setValue]);

  useEffect(() => {
    if (!entityTypes?.some(item => item.value === LEGAL_ENTITY_TYPES.RECIPIENT)) {
      setValue('attestationNumber', '');
    }
  }, [entityTypes, setValue]);

  const onFormSubmit = (data: EntityView) => {
    const possibleTypes = [LEGAL_ENTITY_TYPES.SUPPLIER];
    if (canHandleRecipient) {
      possibleTypes.push(LEGAL_ENTITY_TYPES.RECIPIENT);
    }
    const submitData: any = {
      address: {
        address1: data.address1 || '',
        address2: data.address2 || '',
        city: data.city || '',
        postalCode: data.postalCode || '',
        country: data.country || null
      },
      attestationNumber: data.attestationNumber || '',
      entityTypes: data.entityTypes || [],
      firstName: data.institutionName || '',
      institutionIdentifiers: identifierFields,
      institutionName: data.institutionName || '',
      possibleTypes
    };
    const addressEmpty = !submitData.address.address1
        && !submitData.address.address2
        && !submitData.address.city
        && !submitData.address.postalCode
        && !submitData.address.country;

    if (addressEmpty) {
      submitData.address = {};
    }

    console.log('Payload envoyé:', submitData);
    onSubmit(submitData);
  };

  const popupMessage = useMemo(() => {
    if (entityId) {
      return canHandleRecipient ? 'modalSupplierOrRecipient.editSupplierOrRecipient' : 'modalSupplierOrRecipient.editSupplier';
    }
    return canHandleRecipient ? 'modalSupplierOrRecipient.newSupplierOrRecipient' : 'modalSupplierOrRecipient.newSupplier';
  }, [canHandleRecipient, entityId]);

  return (
    <form autoComplete="off" onSubmit={handleSubmit(onFormSubmit)}>
      <ModalHeader onClose={onClose}>
        <FlexContainer alignItems="center" justifyContent="space-between">
          <div>
            <FontAwesomeIcon icon={faBuilding} />
            {translate(popupMessage)}
          </div>
        </FlexContainer>
      </ModalHeader>

      {isLoading ? (
        <SkeletonForm />
        ) : (
          <>
            <DialogContent style={{ minWidth: '700px', padding: '10px' }}>
              <FlexContainer flexDirection="column">
                {canHandleRecipient && (
                  <>
                    <FormLine>
                      <div style={{ flexGrow: 10 }}>
                        <Controller
                                control={control}
                                defaultValue={[]}
                                name="entityTypes"
                                render={({ field }) => (
                                  <SelectField
                                        {...field}
                                        closeMenuOnSelect={undefined}
                                        id="entity-type"
                                        isClearable={undefined}
                                        isMulti
                                        isSearchable
                                        label="common.legalEntityType"
                                        name="entity-type"
                                        noOptionsMessage={undefined}
                                        options={[
                                          legalEntityTypeToSelectItem(LEGAL_ENTITY_TYPES.SUPPLIER),
                                          legalEntityTypeToSelectItem(LEGAL_ENTITY_TYPES.RECIPIENT)
                                        ]}
                                        placeholder={undefined}
                                        required
                                        value={field.value || []}
                                        width={undefined}
                                    />
                                )}
                            />
                      </div>
                      <div style={{ marginBottom: '1rem' }}>
                        <CustomTooltip
                                icon={faInfoCircle}
                                text={translate('modalSupplierOrRecipient.informationFluidProcessingPlant')}
                            />
                      </div>
                    </FormLine>
                    <RequiredSelectHack value={entityTypes ?? ''} />
                  </>
                  )}

                <FormCountryIdentifier
                      clearErrors={clearErrors}
                      columnSize={12}
                      control={control}
                      getValues={getValues}
                      identifierFields={identifierFields}
                      setIdentifierFields={setIdentifierFields}
                      setValue={setValue}
                      watch={watch}
                  />

                {entityTypes?.some(item => item.value === LEGAL_ENTITY_TYPES.RECIPIENT) && (
                  <Controller
                          control={control}
                          name="attestationNumber"
                          render={({ field }) => (
                            <TextField
                                  {...field}
                                  autoComplete="new-password"
                                  error={!!errors.attestationNumber}
                                  label={translate('common.attestationNumber')}
                                  required
                              />
                          )}
                      />
                  )}

                <FormAddress
                      control={control}
                      getValues={getValues}
                      isRequired
                      setValue={setValue}
                      watch={watch}
                      withCountry={false}
                  />
                {/* This input is only used for forcing disabling autocomplete in chrome browser */}
                <input name="fakeInputForForcingDisablingAutocompleteChromes" style={{ display: 'none' }} type="text" />
              </FlexContainer>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose}>{translate('button.cancel')}</Button>
              <Button color="primary" type="submit">{translate('button.save')}</Button>
            </DialogActions>
          </>
        )}
    </form>
  );
};
